import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useValidationManager, useAsyncState, useAppSelector } from '@/utils/hooks';
import { getCatalogGroup, getLinkableProjects } from '@/services/profile-catalog-groups';
import { getProfileCatalogProductsByGroup, getConflictsWithProjectCatalog } from '@/services/profile-catalog-products';
import { LinkToUnitChooseProject } from './LinkToUnitChooseProject';
import { ValidationSummary } from '@/components/forms/validation';
import { LinkToUnitProducts } from './LinkToUnitProducts';
import { ProjectDeadlineSelect } from '@/seller/components/project/ProjectSettings/ProjectDeadlineSelect';
import { LinkToUnitFinishModal } from './LinkToUnitFinishModal';
import { LoadingIndicator, StepWizard } from '@ygtechnologies/bygr-frontend-component-library';
import { LinkToUnitName } from './LinkToUnitName';
import { TagInput } from '@/components/TagInput';
import { ProjectTagType, OptionalId, ProjectTag, ProfileCatalogProduct, OptionGroupDto, LinkingType } from '@/types';
import { LinkToUnitCompanyProfile } from './LinkToUnitCompanyProfile';
import { LinkToUnitPriceFeeSet } from './LinkToUnitPriceFeeSet';
import { LinkToUnitPriceModifier } from './LinkToUnitPriceModifier';
import { SimplePageTitle } from '@/components/layout/PageTitle';
import { UnitSelector } from '../../UnitSelector';
import { AlertBanner, Card, Checkbox } from '@ygtechnologies/rubus-ui';
import { OptionGroupUpdateParams, RoomLinkUpdateParams } from '@/services/options/option-groups';
import { OptionItemCreateParams } from '@/services/options/option-items';
import { useSubmitDirectWizard, useSubmitRoomWizard } from '@/services/options/option-wizard.hooks';
import { LargeRadio } from '@/components/forms/Radio';
import { OptionRoomLinkEditor } from '../../options/OptionRoomLinkEditor';
import { useQuery } from '@tanstack/react-query';
import { getRooms } from '@/services/rooms';
import { getResidentialUnitsByProjectId } from '@/services/residential-units';

export interface LinkStepWizardProps {
    productGroupId: number;
}

export const LinkStepWizard: React.FC<LinkStepWizardProps> = ({ productGroupId }) => {
    const defaultProjectId = useAppSelector((state) => state.global.activeProject?.id ?? null);
    const defaultProfileId = useAppSelector((state) => state.global.activeProfile?.id ?? null);

    const [products] = useAsyncState(() => getProfileCatalogProductsByGroup(productGroupId), [productGroupId], null);
    const [catalogGroup] = useAsyncState(() => getCatalogGroup(productGroupId), [productGroupId], null);
    // Loaded here and passed to the project picker, so the selected project's pricing mode
    // is available without a separate per-project fetch.
    const [linkableProjects] = useAsyncState(() => getLinkableProjects(productGroupId), [productGroupId], null);

    const [name, setName] = useState('');
    const [description, setDescription] = useState('');

    const [selectedProjectId, setSelectedProjectId] = useState<number | null>(defaultProjectId || null);
    const [priceFieldId, setPriceFieldId] = useState<number | null>(null);
    const [showQuantityToBuyers, setShowQuantityToBuyers] = useState(false);

    const [prices, setPrices] = useState<{ id: number; price: number | null }[]>([]);
    const [defaultProductId, setDefaultProductId] = useState<number | null>(null);
    const [deadlineId, setDeadlineId] = useState<number | null>(null);
    const [showFinishModal, setShowFinishModal] = useState(false);
    const [residentialUnitIds, setResidentialUnitIds] = useState<number[]>([]);
    const [rooms, setRooms] = useState<RoomLinkUpdateParams[]>([]);
    const [selectedTags, setSelectedTags] = useState<OptionalId<ProjectTag>[]>([]);
    const [providedByProfileId, setProvidedByProfileId] = useState<number | null>(defaultProfileId);
    const [projectFeeSetId, setProjectFeeSetId] = useState<number | null>(null);
    const [conflictingProducts, setConflictingProducts] = useState<ProfileCatalogProduct[]>([]);
    const [linkingType, setLinkingType] = useState(LinkingType.Room);

    const roomWizardMutation = useSubmitRoomWizard();
    const directWizardMutation = useSubmitDirectWizard();

    useEffect(() => {
        async function checkExistingProducts() {
            if (!products || !selectedProjectId) {
                setConflictingProducts([]);
                return;
            }

            setConflictingProducts(
                await getConflictsWithProjectCatalog(
                    selectedProjectId,
                    products.filter((x) => !x.isNone).map((x) => x.id)
                )
            );
        }

        checkExistingProducts();
    }, [selectedProjectId, products]);

    const [isSaving, setIsSaving] = useState(false);
    const selectedTagIds = useMemo(
        () => selectedTags.map((x) => x.id).filter((id): id is number => typeof id === 'number'),
        [selectedTags]
    );

    const { data: projectRooms } = useQuery({
        queryKey: ['catalog-link-project-rooms', selectedProjectId],
        queryFn: () => getRooms(selectedProjectId!),
        enabled: !!selectedProjectId,
    });

    const { data: projectUnits } = useQuery({
        queryKey: ['catalog-link-project-units', selectedProjectId],
        queryFn: () => getResidentialUnitsByProjectId(selectedProjectId!),
        enabled: !!selectedProjectId,
    });

    // Pricing mode for the selected project, read from the linkable-projects list (no extra
    // fetch). Undefined until the list loads or before a project is chosen, so seeding waits.
    const projectMode = linkableProjects?.find((p) => p.id === selectedProjectId)?.absolutePricing;

    useEffect(() => {
        if (catalogGroup) {
            setName(catalogGroup.name);
        }
    }, [catalogGroup]);

    useEffect(() => {
        if (!priceFieldId) {
            setShowQuantityToBuyers(false);
        }
    }, [priceFieldId]);

    // Pick an initial default (first product) once products load.
    useEffect(() => {
        if (products && products.length > 0 && defaultProductId === null) {
            setDefaultProductId(products[0].id);
        }
    }, [products, defaultProductId]);

    // Seed prices once the project's pricing mode is known. In absolute mode the stored
    // price is what the buyer sees, so offset every product by the default (products[0],
    // the initial default); in relative mode store the real catalog price. Keyed on the
    // product set and resolved mode only - NOT the selected default - so changing the
    // default later (handleSetDefaultProduct shifts prices) does not wipe manual edits.
    useEffect(() => {
        if (!products || products.length === 0 || projectMode === undefined) return;
        const baseline = projectMode ? products[0].defaultPrice || 0 : 0;
        setPrices(products.map((p) => ({ id: p.id, price: (p.defaultPrice || 0) - baseline })));
    }, [products, projectMode]);

    const handleProjectChange = useCallback((projectId: number | null) => {
        setDeadlineId(null);
        setResidentialUnitIds([]);
        setRooms([]);
        setSelectedTags([]);
        setSelectedProjectId(projectId);
    }, []);

    useEffect(() => {
        handleProjectChange(defaultProjectId);
    }, [handleProjectChange, defaultProjectId]);

    useEffect(() => {
        setProvidedByProfileId(defaultProfileId);
    }, [defaultProfileId]);

    const roomLinkingDisabled = !!selectedProjectId && !!projectRooms && projectRooms.length === 0;

    useEffect(() => {
        if (roomLinkingDisabled && linkingType === LinkingType.Room) {
            setLinkingType(LinkingType.Direct);
        }
    }, [roomLinkingDisabled, linkingType]);

    useEffect(() => {
        if (!selectedProjectId || !projectUnits || selectedTagIds.length === 0) {
            return;
        }

        setLinkingType(LinkingType.Direct);
        setResidentialUnitIds(
            projectUnits
                .filter((unit) => unit.projectTags.some((tag) => selectedTagIds.includes(tag.id)))
                .map((unit) => unit.id)
        );
    }, [selectedProjectId, projectUnits, selectedTagIds]);

    const mappedProducts = (products || []).map((p) => {
        const price = prices.find((_price) => _price.id === p.id);
        return { price: price ? price.price : 0, ...p };
    });

    const setPrice = (id: number, price: number | null) => {
        setPrices(prices.map((p) => (id === p.id ? { id, price } : p)));
    };

    // Changing the default re-bases absolute offsets: shift every price by the change in
    // the default's catalog price so the new default lands at 0 and manual edits survive.
    // In relative mode the stored prices are the real prices, so only the flag changes.
    const handleSetDefaultProduct = (id: number | null) => {
        if (projectMode && id !== null && defaultProductId !== null && products) {
            const oldDefault = products.find((p) => p.id === defaultProductId);
            const newDefault = products.find((p) => p.id === id);
            const delta = (newDefault?.defaultPrice || 0) - (oldDefault?.defaultPrice || 0);
            if (delta !== 0) {
                setPrices((prev) => prev.map((x) => ({ ...x, price: (x.price ?? 0) - delta })));
            }
        }
        setDefaultProductId(id);
    };

    const validationManager = useValidationManager();
    const validationManagerName = useValidationManager();
    const validationManagerDeadline = useValidationManager([
        {
            isValid: () => {
                return !!deadlineId;
            },
            message: () => <Trans>Deadline is required</Trans>,
            summaryMessage: () => <Trans>Deadline is required</Trans>,
        },
    ]);

    const handleSubmit = async () => {
        if (!selectedProjectId || !deadlineId || !projectFeeSetId) {
            return;
        }

        setIsSaving(true);
        try {
            const group: OptionGroupUpdateParams = {
                projectId: selectedProjectId,
                deadlineId: deadlineId,
                name: name,
                description: description,
                projectFeeSetId: projectFeeSetId,
                providedByProfileId: providedByProfileId,
                disableManualSelection: false,
                hidePrice: false,
                makeFree: false,
                allowManualQuantity: false,
                showQuantityToBuyers: false,
                projectTagId: null,
            };

            const options = prices.map<OptionItemCreateParams>((x, index) => ({
                isDefault: defaultProductId === x.id,
                price: x.price || 0,
                profileProductId: x.id,
                sortOrder: index,
                isArchived: false,
            }));

            let result: OptionGroupDto;

            if (linkingType === LinkingType.Room) {
                result = await roomWizardMutation.mutateAsync({
                    optionGroup: group,
                    optionItems: options,
                    roomLinks: rooms.map((x) => ({
                        pricingFieldId: x.pricingFieldId,
                        roomId: x.roomId,
                    })),
                    catalogGroupId: productGroupId,
                });
            } else if (linkingType === LinkingType.Direct) {
                result = await directWizardMutation.mutateAsync({
                    optionGroup: {
                        ...group,
                        pricingVariableId: priceFieldId,
                        roomId: 0,
                    },
                    optionItems: options,
                    linkedResidentialUnitIds: residentialUnitIds,
                    catalogGroupId: productGroupId,
                });
            } else {
                throw Error('Invalid LinkingType');
            }

            setShowFinishModal(true);
        } finally {
            setIsSaving(false);
        }
    };

    const onSelectionChange = useCallback((selection) => {
        setResidentialUnitIds(selection);
    }, []);

    if (!catalogGroup) {
        return <LoadingIndicator />;
    }

    const hasProject = !!selectedProjectId;

    const conflictingProductNames = conflictingProducts.map((x) => x.name).join(', ');

    return (
        <>
            <SimplePageTitle title={t`Link "${catalogGroup.name}" to project`} />

            <StepWizard>
                <StepWizard.Step validationManager={validationManager} title={<Trans>Choose project</Trans>}>
                    <Card>
                        <label>
                            <Trans>To which project would you like to offer these products?</Trans>
                        </label>

                        <LinkToUnitChooseProject
                            value={selectedProjectId}
                            onChange={handleProjectChange}
                            projects={linkableProjects ?? []}
                        />

                        {conflictingProducts.length > 0 && (
                            <AlertBanner type="warning">
                                <Trans>
                                    Note that the products {conflictingProductNames} in this product group has already been
                                    uploaded to the selected project.
                                    <br />
                                    If you reconnect these products to the project, the associated products in the project
                                    will not update even if they now are edited.
                                    <ul style={{ margin: 0, paddingLeft: '16px' }}>
                                        <li>
                                            If you want to edit the products that were previously uploaded to the project,
                                            navigate to "options" and on to "products".
                                        </li>
                                        <li>
                                            If you want to upload these products again with updated text or content, you must
                                            first delete the products in the product catalog of the project.
                                        </li>
                                    </ul>
                                </Trans>
                            </AlertBanner>
                        )}

                        <ValidationSummary />
                    </Card>
                </StepWizard.Step>

                <StepWizard.Step validationManager={validationManagerName} title={<Trans>Choose option name</Trans>}>
                    <Card>
                        <LinkToUnitName
                            name={name}
                            description={description}
                            onNameChange={setName}
                            onDescriptionChange={setDescription}
                        />
                    </Card>
                </StepWizard.Step>

                <StepWizard.Step title={<Trans>Company profile</Trans>}>
                    <Card>
                        <label>
                            <Trans>
                                Choose the company that will be shown in the option. Usually you choose the company profile
                                of the business that delivers this option - for example electrician or plumber. You may also
                                choose to have no company profile visible.
                            </Trans>
                        </label>
                        <LinkToUnitCompanyProfile
                            onProvidedByProfileIdChange={setProvidedByProfileId}
                            providedByProfileId={providedByProfileId}
                            projectId={selectedProjectId}
                        />
                    </Card>
                </StepWizard.Step>

                <StepWizard.Step validationManager={validationManagerDeadline} title={<Trans>Choose deadline</Trans>}>
                    <Card>
                        <label>
                            <Trans>Choose a deadline. Buyers will not be able change their choices after this date.</Trans>
                        </label>
                        {hasProject && (
                            <ProjectDeadlineSelect
                                projectId={selectedProjectId}
                                value={deadlineId}
                                onChange={(deadlineId) => setDeadlineId(deadlineId)}
                            />
                        )}
                        <ValidationSummary />
                    </Card>
                </StepWizard.Step>

                <StepWizard.Step disableNext={projectFeeSetId === null} title={<Trans>Select fee preset</Trans>}>
                    <Card>
                        <label>
                            <Trans>Select fee preset</Trans>
                        </label>

                        <LinkToUnitPriceFeeSet
                            selectedProjectId={selectedProjectId}
                            onChange={setProjectFeeSetId}
                            value={projectFeeSetId}
                        />
                    </Card>
                </StepWizard.Step>

                <StepWizard.Step title={<Trans>Choose prices and default choices</Trans>}>
                    <LinkToUnitProducts
                        setDefaultProductId={handleSetDefaultProduct}
                        defaultProductId={defaultProductId}
                        setPrice={setPrice}
                        products={mappedProducts}
                        projectFeeSetId={projectFeeSetId}
                        projectId={selectedProjectId}
                    />
                </StepWizard.Step>

                <StepWizard.Step title={<Trans>Choose linking type</Trans>}>
                    <Card>
                        <LargeRadio
                            disabled={roomLinkingDisabled}
                            onChange={() => setLinkingType(LinkingType.Room)}
                            checked={linkingType === LinkingType.Room}>
                            <Trans>
                                <strong>Room linking</strong>
                                The option group will be linked to rooms (recommended).
                            </Trans>
                        </LargeRadio>

                        <LargeRadio
                            onChange={() => setLinkingType(LinkingType.Direct)}
                            checked={linkingType === LinkingType.Direct}>
                            <Trans>
                                <strong>Direct linking</strong>
                                The option group will be linked to units directly.
                            </Trans>
                        </LargeRadio>
                        <AlertBanner type="info">
                            <Trans>
                                Room linking is recommended to make the option process more intuitive for your customers, as
                                they will be able to make selections room by room.
                            </Trans>
                        </AlertBanner>
                        {roomLinkingDisabled && (
                            <AlertBanner type="warning">
                                <Trans>
                                    Room linking is unavailable for this project because it has no rooms. Use direct linking
                                    instead.
                                </Trans>
                            </AlertBanner>
                        )}

                        <label>
                            <Trans>
                                Choose room tags to preselect matching units if you want to use direct linking from the
                                start.
                            </Trans>
                        </label>
                        {selectedTags.length > 1 && (
                            <AlertBanner type="info">
                                <Trans>
                                    <strong>Please note</strong> it is usually a good idea to use only one room tag when
                                    linking an option. If you would like to offer this option for several rooms in the
                                    apartment, use the 'Duplicate option' feature after creating the option for this room.
                                </Trans>
                            </AlertBanner>
                        )}
                        <TagInput
                            value={selectedTags}
                            type={ProjectTagType.Room}
                            onTagsChange={setSelectedTags}
                            projectId={selectedProjectId}
                        />
                    </Card>
                </StepWizard.Step>

                {linkingType === LinkingType.Direct ? (
                    <StepWizard.Step title={<Trans>Choose price modifier</Trans>}>
                        <Card>
                            <LinkToUnitPriceModifier
                                selectedPriceFieldId={priceFieldId}
                                selectedProjectId={selectedProjectId}
                                onChange={setPriceFieldId}
                            />

                            <div className="form-group">
                                <span>
                                    <Checkbox
                                        disabled={!priceFieldId}
                                        checked={showQuantityToBuyers}
                                        onChange={(e) => setShowQuantityToBuyers(e.target.checked)}>
                                        <Trans>Show quantity to buyers</Trans>
                                    </Checkbox>
                                </span>
                            </div>
                        </Card>
                    </StepWizard.Step>
                ) : null}

                {linkingType === LinkingType.Direct ? (
                    <StepWizard.Step
                        disableNext={residentialUnitIds.length === 0 || isSaving}
                        onNextCallBack={handleSubmit}
                        nextText={<Trans>Link to units</Trans>}
                        title={<Trans>Choose units</Trans>}>
                        <Card>
                            <label>
                                <Trans>Select the units you want to link to this option group</Trans>
                            </label>
                            {hasProject && selectedProjectId && (
                                <UnitSelector
                                    projectId={selectedProjectId}
                                    selectedResidentialUnitIds={residentialUnitIds}
                                    onChange={onSelectionChange}
                                />
                            )}
                        </Card>
                    </StepWizard.Step>
                ) : null}

                {linkingType === LinkingType.Room ? (
                    <StepWizard.Step
                        disableNext={rooms.length === 0 || isSaving}
                        onNextCallBack={handleSubmit}
                        nextText={<Trans>Link to units</Trans>}
                        title={<Trans>Choose rooms</Trans>}>
                        <Card>
                            <label>
                                <Trans>Select the rooms you want to link to this option group</Trans>
                            </label>
                            {hasProject && selectedProjectId && (
                                <OptionRoomLinkEditor
                                    projectId={selectedProjectId}
                                    value={rooms}
                                    onChange={setRooms}
                                    fillContainer={false}
                                />
                            )}
                        </Card>
                    </StepWizard.Step>
                ) : null}
            </StepWizard>
            {showFinishModal && <LinkToUnitFinishModal />}
        </>
    );
};
